import React, { useRef, useState } from "react";
import { Button } from "../../ui/button";
import { getAdminToken } from "../../../lib/adminApi";
import { toast } from "sonner";
import {
  Heading1, Heading2, Bold, Italic, Code, Quote,
  ListOrdered, List, Minus, Hash, Link2,
  AlignLeft, AlignCenter, AlignRight, Indent,
  Image as ImageIcon, Save, Loader2
} from "lucide-react";

interface MarkdownToolbarProps {
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  content: string;
  onContentChange: (val: string) => void;
  onSave: () => void;
  saving: boolean;
}

function ToolbarButton({ label, icon: Icon, onClick, className }: any) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={onClick}
      title={label}
      className={`h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-muted/80 rounded-lg ${className || ""}`}
    >
      <Icon className="h-4.5 w-4.5" />
    </Button>
  );
}

export function MarkdownToolbar({
  textareaRef,
  content,
  onContentChange,
  onSave,
  saving
}: MarkdownToolbarProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const insertMarkdown = (before: string, after = "") => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;

    const selectedText = text.substring(start, end);
    const replacement = before + (selectedText || "") + after;

    onContentChange(text.substring(0, start) + replacement + text.substring(end));

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + before.length,
        start + before.length + (selectedText ? selectedText.length : 0)
      );
    }, 0);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const token = getAdminToken();
    try {
      const res = await fetch(`/api/admin/upload?filename=${encodeURIComponent(file.name)}`, {
        method: "POST",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          "Content-Type": file.type || "application/octet-stream",
          "X-Filename": encodeURIComponent(file.name),
        },
        body: file
      });

      const data = await res.json() as any;
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to upload image");
      }

      insertMarkdown(`\n![${file.name.split(".")[0]}](${data.url})\n`);
      toast.success("Image uploaded and inserted successfully.");
    } catch (err: any) {
      toast.error(err.message || "Image upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const tools = [
    { label: "Heading 1", icon: Heading1, action: () => insertMarkdown("\n# ", "\n") },
    { label: "Heading 2", icon: Heading2, action: () => insertMarkdown("\n## ", "\n") },
    { label: "Bold", icon: Bold, action: () => insertMarkdown("**", "**") },
    { label: "Italic", icon: Italic, action: () => insertMarkdown("*", "*") },
    { label: "Code Block", icon: Code, action: () => insertMarkdown("\n```javascript\n", "\n```\n") },
    { label: "Blockquote", icon: Quote, action: () => insertMarkdown("\n> ") },
    { label: "Numbered List", icon: ListOrdered, action: () => insertMarkdown("\n1. ") },
    { label: "Bulleted List", icon: List, action: () => insertMarkdown("\n- ") },
    { label: "Divider", icon: Minus, action: () => insertMarkdown("\n---\n") },
    { label: "Footnote", icon: Hash, action: () => insertMarkdown("[^1]", "\n\n[^1]: Footnote content") },
    { label: "Link", icon: Link2, action: () => insertMarkdown("[", "](url)") },
    { label: "Align Left", icon: AlignLeft, action: () => insertMarkdown('\n<div align="left">\n', '\n</div>\n') },
    { label: "Align Center", icon: AlignCenter, action: () => insertMarkdown('\n<div align="center">\n', '\n</div>\n') },
    { label: "Align Right", icon: AlignRight, action: () => insertMarkdown('\n<div align="right">\n', '\n</div>\n') },
    { label: "Indent", icon: Indent, action: () => insertMarkdown("    ") },
  ];

  return (
    <div className="sticky top-0 z-20 flex flex-wrap items-center gap-1 p-2 border-b border-border/80 bg-card/95 backdrop-blur-md rounded-t-xl select-none">
      {tools.map((t, idx) => (
        <ToolbarButton key={idx} label={t.label} icon={t.icon} onClick={t.action} />
      ))}

      <div className="h-6 w-px bg-border/80 mx-1"></div>

      <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" className="hidden" />

      <Button
        type="button"
        variant="ghost"
        size="icon"
        disabled={uploading}
        onClick={() => fileInputRef.current?.click()}
        title="Insert Image (Upload to R2)"
        className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-muted/80 rounded-lg"
      >
        {uploading ? <Loader2 className="h-4.5 w-4.5 animate-spin" /> : <ImageIcon className="h-4.5 w-4.5" />}
      </Button>

      <div className="h-6 w-px bg-border/80 mx-1"></div>

      <ToolbarButton label="Quick Save" icon={Save} onClick={onSave} className="text-brand-primary hover:text-brand-primary hover:bg-brand-primary/10 ml-auto" disabled={saving} />
    </div>
  );
}
export default MarkdownToolbar;
